import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from './useConfirm';
import { drbfmService } from '../services/drbfm.service';
import { resolveApiErrorMessage } from '../utils/apiErrorMessages';
import type {
  DRBFMCase,
  DRBFMCaseDesignChange,
  DRBFMCaseWorkflowStep,
  DRBFMCaseInputData,
  DRBFMCasePrecheckItem,
  SessionResultsResponse,
} from '../types/drbfm';

interface UseDRBFMCaseWorkflowResult {
  caseStep: DRBFMCaseWorkflowStep;
  currentCase: DRBFMCase | null;
  currentCaseId: string | null;
  designChanges: DRBFMCaseDesignChange[];
  precheckItems: DRBFMCasePrecheckItem[];
  results: SessionResultsResponse | null;
  latestAnalysisSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  createAndExtract: (input: DRBFMCaseInputData) => Promise<DRBFMCase | null>;
  saveChangesAndGeneratePrecheck: (
    changes: DRBFMCaseDesignChange[],
  ) => Promise<DRBFMCasePrecheckItem[]>;
  saveAnswersAndAnalyze: (
    items: DRBFMCasePrecheckItem[],
  ) => Promise<SessionResultsResponse | null>;
  retryFetchResults: () => Promise<SessionResultsResponse | null>;
  reset: () => void;
  setDesignChanges: Dispatch<SetStateAction<DRBFMCaseDesignChange[]>>;
  setPrecheckItems: Dispatch<SetStateAction<DRBFMCasePrecheckItem[]>>;
  setCaseStep: Dispatch<SetStateAction<DRBFMCaseWorkflowStep>>;
  setCurrentCaseIdForTest: Dispatch<SetStateAction<string | null>>;
}

const INITIAL_STEP: DRBFMCaseWorkflowStep = 'case-intake';

export const useDRBFMCaseWorkflow = (): UseDRBFMCaseWorkflowResult => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [caseStep, setCaseStep] = useState<DRBFMCaseWorkflowStep>(INITIAL_STEP);
  const [currentCase, setCurrentCase] = useState<DRBFMCase | null>(null);
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);
  const currentCaseIdRef = useRef<string | null>(null);
  const [designChanges, setDesignChanges] = useState<DRBFMCaseDesignChange[]>([]);
  const [precheckItems, setPrecheckItems] = useState<DRBFMCasePrecheckItem[]>([]);
  const [results, setResults] = useState<SessionResultsResponse | null>(null);
  const [latestAnalysisSessionId, setLatestAnalysisSessionId] = useState<string | null>(null);
  const latestAnalysisSessionIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setCurrentCaseIdWithRef = useCallback((
    value: SetStateAction<string | null>,
  ) => {
    if (typeof value === 'function') {
      setCurrentCaseId((previous) => {
        const nextValue = value(previous);
        currentCaseIdRef.current = nextValue;
        return nextValue;
      });
      return;
    }

    currentCaseIdRef.current = value;
    setCurrentCaseId(value);
  }, []);

  const setLatestAnalysisSessionIdWithRef = useCallback((
    value: string | null,
  ) => {
    latestAnalysisSessionIdRef.current = value;
    setLatestAnalysisSessionId(value);
  }, []);

  const clearCaseState = useCallback(() => {
    setCurrentCase(null);
    setCurrentCaseIdWithRef(null);
    setDesignChanges([]);
    setPrecheckItems([]);
    setResults(null);
    setLatestAnalysisSessionIdWithRef(null);
  }, [setCurrentCaseIdWithRef, setLatestAnalysisSessionIdWithRef]);

  const createAndExtract = useCallback(async (
    input: DRBFMCaseInputData,
  ): Promise<DRBFMCase | null> => {
    setIsLoading(true);
    setError(null);
    setCaseStep(INITIAL_STEP);
    clearCaseState();

    try {
      const createdCase = await drbfmService.createCase(input);
      setCurrentCase(createdCase);
      setCurrentCaseIdWithRef(createdCase.id);

      const extractedChanges = await drbfmService.extractCaseChanges(createdCase.id);
      setDesignChanges(extractedChanges);
      setCaseStep('change-review');

      return createdCase;
    } catch (err) {
      clearCaseState();
      setCaseStep(INITIAL_STEP);
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.createCaseFailed' }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [clearCaseState, setCurrentCaseIdWithRef, t]);

  const saveChangesAndGeneratePrecheck = useCallback(async (
    changes: DRBFMCaseDesignChange[],
  ): Promise<DRBFMCasePrecheckItem[]> => {
    const caseId = currentCaseIdRef.current;
    if (!caseId) {
      setError(t('drbfm.case.missingCase'));
      return [];
    }

    if (precheckItems.length > 0 && !(await confirm({
      title: t('drbfm.confirm.regeneratePrecheck.title'),
      description: t('drbfm.confirm.regeneratePrecheck.description'),
      confirmLabel: t('drbfm.confirm.regeneratePrecheck.confirm'),
    }))) {
      return precheckItems;
    }

    setIsLoading(true);
    setError(null);
    setPrecheckItems([]);
    setResults(null);
    setLatestAnalysisSessionIdWithRef(null);

    try {
      const savedChanges = await drbfmService.saveCaseDesignChanges(caseId, changes);
      setDesignChanges(savedChanges);

      const generatedItems = await drbfmService.generateCasePrecheck(caseId);
      setPrecheckItems(generatedItems);
      setCaseStep('precheck');

      return generatedItems;
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.generatePrecheckFailed' }));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [confirm, precheckItems, setLatestAnalysisSessionIdWithRef, t]);

  const saveAnswersAndAnalyze = useCallback(async (
    items: DRBFMCasePrecheckItem[],
  ): Promise<SessionResultsResponse | null> => {
    const caseId = currentCaseIdRef.current;
    if (!caseId) {
      setError(t('drbfm.case.missingCase'));
      return null;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);
    setLatestAnalysisSessionIdWithRef(null);
    setCaseStep('analysis');

    try {
      const savedItems = await drbfmService.saveCasePrecheckAnswers(caseId, items);
      setPrecheckItems(savedItems);

      const analysis = await drbfmService.analyzeCase(caseId);
      setLatestAnalysisSessionIdWithRef(analysis.sessionId);
      try {
        const sessionResults = await drbfmService.getSessionResults(analysis.sessionId);
        setResults(sessionResults);
        setCaseStep('results');

        return sessionResults;
      } catch (err) {
        setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.fetchResultsFailed' }));
        setCaseStep('results');
        return null;
      }
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.analyzeFailed' }));
      setCaseStep('precheck');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [setLatestAnalysisSessionIdWithRef, t]);

  const retryFetchResults = useCallback(async (): Promise<SessionResultsResponse | null> => {
    const sessionId = latestAnalysisSessionIdRef.current;
    if (!sessionId) {
      setError(t('drbfm.case.missingAnalysisSession'));
      return null;
    }

    setIsLoading(true);
    setError(null);
    setCaseStep('results');

    try {
      const sessionResults = await drbfmService.getSessionResults(sessionId);
      setResults(sessionResults);
      return sessionResults;
    } catch (err) {
      setResults(null);
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.fetchResultsFailed' }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const reset = useCallback(() => {
    setCaseStep(INITIAL_STEP);
    clearCaseState();
    setIsLoading(false);
    setError(null);
  }, [clearCaseState]);

  return {
    caseStep,
    currentCase,
    currentCaseId,
    designChanges,
    precheckItems,
    results,
    latestAnalysisSessionId,
    isLoading,
    error,
    createAndExtract,
    saveChangesAndGeneratePrecheck,
    saveAnswersAndAnalyze,
    retryFetchResults,
    reset,
    setDesignChanges,
    setPrecheckItems,
    setCaseStep,
    setCurrentCaseIdForTest: setCurrentCaseIdWithRef,
  };
};
